import { useEffect, useId, useState } from "react";
import { Activity, Check, X } from "lucide-react";
import { Botao, BotaoIcone, IconeTema } from "@/Logigate/ui";

/**
 * O aviso a quem trabalha numa empresa com o módulo Monitoramento — uma vez por empresa, por
 * browser (DECISOES §3).
 *
 * A web registava IP, dispositivo e páginas com hora sem uma linha de aviso em lado nenhum (A19).
 * A obrigação de informar é da empresa; o painel é onde a pessoa está. Não bloqueia o trabalho e
 * não tem «desligar»: o registo é da empresa. É o mesmo texto que a app mostra.
 *
 * É um cartão sem posição própria: o `MainProvider` põe-no no canto, com os outros avisos.
 */
interface Props {
    empresaId: number;
}

const GARANTIAS = [
    "Não é registada a localização GPS.",
    "Não se regista nada com o separador escondido nem com o painel fechado.",
    "Os termos das pesquisas não ficam no histórico de páginas.",
];

export default function AvisoPrivacidadeMonitoramento({ empresaId }: Props) {
    const chave = `monitoramento_aviso_visto_${empresaId}`;
    const idTitulo = useId();
    const [aberto, setAberto] = useState(false);

    useEffect(() => {
        if (!empresaId) return;
        try {
            setAberto(window.localStorage.getItem(chave) !== "1");
        } catch {
            setAberto(true);
        }
    }, [chave, empresaId]);

    const fechar = () => {
        try {
            window.localStorage.setItem(chave, "1");
        } catch {
            // Sem localStorage volta a aparecer na próxima visita — é melhor do que nunca aparecer.
        }
        setAberto(false);
    };

    if (!aberto) {
        return null;
    }

    return (
        <section
            role="dialog"
            aria-labelledby={idTitulo}
            className="pointer-events-auto w-full rounded-xl border border-zinc-200 bg-white p-4 shadow-xl shadow-zinc-950/10 animate-in fade-in-0 slide-in-from-bottom-2 md:w-[400px] dark:border-zinc-700 dark:bg-zinc-900"
        >
            <div className="flex items-start gap-3">
                <IconeTema cor="blue" tamanho="md">
                    <Activity />
                </IconeTema>
                <div className="min-w-0 flex-1">
                    <h2 id={idTitulo} className="text-sm font-semibold leading-5 text-zinc-900 dark:text-zinc-50">
                        Esta empresa regista a actividade no painel
                    </h2>
                    <p className="mt-1 text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-300">
                        Quando entra e sai, os ecrãs que abre, o endereço IP e o dispositivo. Os administradores da
                        empresa com permissão para isso podem consultá-lo; cada pessoa vê sempre a sua própria actividade
                        em Monitoramento.
                    </p>
                </div>
                <BotaoIcone rotulo="Fechar" onClick={fechar} className="-mr-1.5 -mt-1.5 size-10 md:size-8">
                    <X className="size-4" />
                </BotaoIcone>
            </div>
            <ul className="mt-3 space-y-1.5 text-xs leading-snug text-zinc-500 dark:text-zinc-400 sm:pl-12">
                {GARANTIAS.map((g) => (
                    <li key={g} className="flex gap-2">
                        <Check className="mt-px size-3.5 shrink-0 text-portal dark:text-portal-claro" aria-hidden />
                        {g}
                    </li>
                ))}
            </ul>
            <div className="mt-4 flex justify-end">
                <Botao tamanho="sm" onClick={fechar} className="h-11 w-full sm:h-8 sm:w-auto">
                    Entendi
                </Botao>
            </div>
        </section>
    );
}
